import type { ConfiguratorOriginsType } from '../constants';
import type {
  ConfigurationMonogramDetails,
  DefaultValues,
  ExistingOrderDetails,
  Fabric,
  HiddenOptionsAndValues,
  Monogram,
  OptionConfigurationType,
  OptionsSelection,
  OptionValue,
  SizePassportSelection,
  StylePreset,
  StylePresets,
  typeFabricList,
  typeOptionValue,
} from '../interfaces';
import { Section } from '../navigation';
import type { ConfigurationTypes, ProductType } from '../productDefinition';
import { getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId } from '../productDefinition';
import { isAdditonalProduct } from '../utils';
import { getPreloadConfigurationDefaultSelections } from './preloadConfiguration';

// Autosave persists the live runtime state directly. Defined here so the same
// validation pipeline used by the server-side preload (clone/reorder) can be
// reused on the client when resuming an autosaved session.
export interface AutosaveData {
  hiddenOptionsAndValues: HiddenOptionsAndValues;
  isAdditionalProductAdded: Record<string, boolean>;
  isManualSelectionDone: boolean;
  section: string | null;
  selectedBaseStyle: Record<string, StylePreset>;
  selectedFabric: Fabric | null;
  selectedMonogram: Monogram;
  selectedOptions: OptionsSelection;
  sizePassportSelection: SizePassportSelection;
}

type ExistingSubConfiguration = ExistingOrderDetails['subConfigurations'][number];

// When an option value id is no longer present in the live optionValues lookup
// we still need to feed it through the validator so it gets reported as missing.
// The validator only reads `id` and `optionId` off the OptionValue when bucketing
// into preselection vs missing, so a stub with those two fields is sufficient.
const synthesizeMissingOptionValue = (
  id: number,
  optionId: number,
  configurationTypeId: number,
): OptionValue => ({
  id,
  optionId,
  configurationTypeId,
  code: '',
  imageName: '',
  name: '',
  description: '',
  imageUrlTemplate: '',
  layerImageName: '',
  measurementTag: null,
  additionalPrice: null,
});

// Maps the autosave runtime shape to the ExistingOrderDetails shape consumed by
// getPreloadConfigurationDefaultSelections. Keeps the validation algorithm in
// one place for both clone/reorder (server) and autosave resume (client).
export const mapAutosaveDataToExistingOrderDetails = (
  autosaveData: AutosaveData,
  product: ProductType,
  optionValues: typeOptionValue,
  options: OptionConfigurationType,
  defaultValues: DefaultValues,
): ExistingOrderDetails => {
  const fabricId = autosaveData.selectedFabric?.id ?? -1;
  const subConfigurations: ExistingSubConfiguration[] = [];

  Object.entries(autosaveData.selectedOptions ?? {}).forEach(([configTypeIdStr, optionMap]) => {
    const configurationTypeId = +configTypeIdStr;
    const baseConfigurationId = getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(configurationTypeId);
    const isExtraProduct = baseConfigurationId !== configurationTypeId;

    // Mirror clone/reorder semantics: extra products only contribute a
    // subConfiguration when they were actually added to the configuration.
    if (isExtraProduct && !autosaveData.isAdditionalProductAdded?.[configurationTypeId]) {
      return;
    }

    // Skip stale autosave entries whose configurationTypeId is no longer part
    // of the current product definitions. Downstream validators (e.g.
    // getExistingConfigurationSelectAndMissingOptionValues) index into
    // options[baseConfigurationId].items and
    // defaultValues.configurationTypes[baseConfigurationId].defaultOptionValues
    // without guards, so letting an unknown id through would crash resume.
    if (!options[baseConfigurationId] || !defaultValues.configurationTypes[baseConfigurationId]) {
      return;
    }

    const optionValuesArr: OptionValue[] = Object.entries(optionMap ?? {}).map(([optionIdStr, optionValueId]) => {
      const lookup = optionValues[optionValueId];
      if (lookup) {
        return lookup;
      }
      return synthesizeMissingOptionValue(+optionValueId, +optionIdStr, baseConfigurationId);
    });

    const monogramDef = options[baseConfigurationId]?.items.find(item => item.type === 'MONOGRAM');
    const monogramsArr: ConfigurationMonogramDetails[] = [];
    if (monogramDef && autosaveData.selectedMonogram) {
      Object.entries(autosaveData.selectedMonogram).forEach(([positionIdStr, monogramItem]) => {
        const positionId = +positionIdStr;
        // Only attach a monogram to this subconfig if its position belongs to
        // this configuration's monogram definition. Positions live in a single
        // configuration so this routes correctly even with multiple subconfigs.
        if (monogramDef.positions?.[positionId]) {
          monogramsArr.push({
            positionId,
            positionName: monogramItem.positionName ?? '',
            colorId: monogramItem.colorId ?? -1,
            colorName: monogramItem.colorName ?? '',
            fontId: monogramItem.fontStyleId ?? -1,
            fontName: monogramItem.fontName ?? '',
            initialsText: monogramItem.initials ?? '',
          });
        }
      });
    }

    const stylePresetId =
      autosaveData.selectedBaseStyle?.[configurationTypeId]?.id ??
      autosaveData.selectedBaseStyle?.[baseConfigurationId]?.id ??
      null;

    const sizePassportId =
      autosaveData.sizePassportSelection?.[configurationTypeId] ??
      autosaveData.sizePassportSelection?.[baseConfigurationId];

    subConfigurations.push({
      id: '',
      configurationTypeId: baseConfigurationId,
      fabricId,
      sizePassportId,
      stylePresetId,
      optionValues: optionValuesArr,
      monograms: monogramsArr,
      isExtraProduct,
      imageIdentifier: '',
      price: { amount: 0, currencyCode: '', symbol: '' },
    });
  });

  return {
    id: '',
    countryCode: '',
    isInStore: true,
    product,
    subConfigurations,
  };
};

// Rebuilds isAdditionalProductAdded from the persisted autosave flags rather
// than from the mapped subConfigurations. The generic preload reconstruction
// (getAdditionalProductSavedConfigurations) only recognizes extra products that
// use the 100+baseId convention (e.g. the Extra Trouser, id 105). Native-id
// additional products such as the Suit waistcoat (id 3) carry isExtraProduct
// === false and their subConfiguration is always present regardless of whether
// the user added the garment, so reconstruction cannot tell whether they were
// added. Autosave persists the authoritative runtime value, so we trust it here
// and only drop entries that are no longer additional products of the current
// product definitions.
const getResumedAdditionalProductsAdded = (
  autosaveData: AutosaveData,
  product: ProductType,
  options: OptionConfigurationType,
  defaultValues: DefaultValues,
): Record<string, boolean> => {
  const resumedAdditionalProductsAdded: Record<string, boolean> = {};

  Object.entries(autosaveData.isAdditionalProductAdded ?? {}).forEach(([configurationTypeId, isAdded]) => {
    if (!isAdded || !isAdditonalProduct(product, configurationTypeId as ConfigurationTypes)) {
      return;
    }

    // Skip stale ids whose base configuration is no longer part of the current
    // product definitions, mirroring the guard in mapAutosaveDataToExistingOrderDetails.
    const baseConfigurationId = getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
    if (!options[baseConfigurationId] || !defaultValues.configurationTypes[baseConfigurationId]) {
      return;
    }

    resumedAdditionalProductsAdded[configurationTypeId] = true;
  });

  return resumedAdditionalProductsAdded;
};

export const getAutosaveResumeDefaultSelections = (
  autosaveData: AutosaveData,
  defaultSection: Section,
  product: ProductType,
  fabrics: typeFabricList,
  options: OptionConfigurationType,
  optionValues: typeOptionValue,
  defaultValues: DefaultValues,
  stylePresets: StylePresets,
  origin: ConfiguratorOriginsType,
) => {
  const savedConfiguration = mapAutosaveDataToExistingOrderDetails(
    autosaveData,
    product,
    optionValues,
    options,
    defaultValues,
  );

  const preloadSelections = getPreloadConfigurationDefaultSelections(
    savedConfiguration,
    defaultSection,
    product,
    fabrics,
    options,
    optionValues,
    defaultValues,
    stylePresets,
    origin,
  );

  return {
    ...preloadSelections,
    isAdditionalProductAdded: getResumedAdditionalProductsAdded(autosaveData, product, options, defaultValues),
  };
};
